import { AbstractDownloadableService } from './abstract-downloadable.service';
import { Guest, Reservation } from '../models';
import { mapGetToPostParams } from '../../app/helpers';

/**
 * @class ReservationPmsApiService
 * @abstract
 * @extends {AbstractDownloadableService<Reservation>}
 */
export class ReservationPmsApiService extends AbstractDownloadableService {

    static get $inject() {

        return super.$inject.concat(['Upload']);
    }

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property/:propertyId/reservation(/:id)';
    }

    constructor(http, config, eventsBroker, DownloadFile, Upload) {

        super(http, config, eventsBroker, DownloadFile);

        this.Upload = Upload;
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {Reservation}
     * @protected
     */
    newModel(attributes, ...params) {

        return new Reservation(attributes);
    }

    /**
     * @param {Object=} params
     * @param {Property=} property
     * @returns {Promise.<PaginationCollection<Reservation>>}
     */
    get(params = {}, property = null) {

        const url = this.url({propertyId: property ? property.id : 0});
        const ignoreLB = params.hasOwnProperty('searchValue');
        return this.http
            .get(url, {
                headers: this.authHeaders,
                params,
                ignoreLoadingBar: ignoreLB
            })
            .then(this.getPaginationCollector())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Reservation} reservation
     * @returns {Promise.<boolean>}
     */
    delete(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    canDelete(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id
        }) + '/can-delete';

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    exportPDF(params = {}, property = null) {

        const url = this.url({propertyId: property ? property.id : 0}) + '/export-pdf';

        return this.download(url, params, 'Reservations.pdf');
    }

    exportExcel(params = {}, property = null) {

        const url = this.url({propertyId: property ? property.id : 0}) + '/export-excel';

        return this.download(url, params, 'Reservations.xls');
    }

    import(file, property = null) {

        const url = this.url({propertyId: property ? property.id : 0}) + '/import';

        return this.Upload
            .upload({
                url: url,
                headers: this.authHeaders,
                data: ({file})
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    bulkCheckIn(reservations, property = null) {

        const url = this.url({propertyId: property ? property.id : 0}) + '/bulk-check-in';
        const data = {
            reservations: reservations
                .map(({id, room_numbers}) => ({
                    'id': id,
                    'accommodations': this.updateAccommodationsMapper(room_numbers),
                }))
        };

        return this.http
            .post(url, data, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    bulkCheckOut(reservations, property = null, forceCheckout = false) {

        const url = this.url({propertyId: property ? property.id : 0}) + '/bulk-check-out';
        const data = {
            reservations: reservations
                .map(({id}) => ({id})),
            force: forceCheckout,
        };

        return this.http
            .post(url, data, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    bulkAddPayment(data, property = null) {

        const url = this.url({propertyId: property ? property.id : 0}) + '/bulk-add-payment';
        data.reservations = (data.reservations || []).map(({id}) => ({id}));

        return this.http
            .post(url, data, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }
    
    /**
     * @param {Object} reservation
     * @param {Property} property
     * @returns {Promise.<Reservation>}
     */
    create(reservation, property) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url, reservation, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    show(propertyId, reservationId, params = {}){

        const url = this.url({
            propertyId: propertyId,
            id: reservationId
        });

        return this.http.get(url, {
            headers: this.authHeaders,
            params
        }).then(this.getModeler())
        .catch(this.getErrorHandler());
    }

    showByIdent(identifier, params = {}) {

        const url = this.url({identifier}, '/reservation-identifier/:identifier');

        return this.http.get(url, {
            headers: this.authHeaders,
            params
        }).then(this.getModeler())
        .catch(this.getErrorHandler());
    }

    updateStatus(reservation, status, emailType = null, cancelReason = null, checkout_date = null, lang = "en") {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/update-status';

        const data = Object.assign({}, emailType, {
            status: status,
            cancel_reason: cancelReason,
            checkout_date: checkout_date,
            lang: lang
        });

        return this.http
            .post(url, data, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    updateArrivalTime(reservation, estimated_arrival_time) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/arrival-time';

        return this.http
            .post(url, ({estimated_arrival_time}), {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    forceCheckout(reservation) {

        const url = this.url({id: reservation.id}, '/reservations/:id/force-checkout');

        return this.http
            .post(url, {}, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }
    
    updateSource(reservation, sourceId) {
        
        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/update-source';
        
        return this.http
            .post(url, {reservation_source_id: sourceId}, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    invoice(reservation, language) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/generate-invoice';

        return this.http
            .post(url, {language}, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    contract(reservation, language){
        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + `/contract?language=${language}`;

        return this.http.get(url, {
            headers: this.authHeaders,
            responseType: 'arraybuffer',
        })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler())
    }

    contractUrl(reservation, language){
        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + `/contract-url?language=${language}`;

        return this.http.get(url, {
            headers: this.authHeaders,
        })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler())
    }

    generateXML(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/generate-xml';

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    emailConfirmation(reservation, emailType = null) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/email-confirmation';

        return this.http
            .post(url, emailType, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    emailInvoicing(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/email-invoicing';

        return this.http
            .post(url, null, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    emailDetails(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/email-details';

        return this.http
            .post(url, null, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    emailFolio(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/email-folio';

        return this.http
            .post(url, null, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    printFolio(reservation, params = {}) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/print-folio';

        return this.http
            .post(url, params, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    printConfirmation(reservation, emailType = null) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/print-confirmation';

        return this.http
            .post(url, emailType, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    guests(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/guests';

        return this.http
            .get(url, {
                headers: this.authHeaders,
            })
            .then(response => this.extractData(response).map(attributes => new Guest(attributes)))
            .catch(this.getErrorHandler());
    }

    checkPreviousRoom(property_id,params) {

        const url = this.url({propertyId: property_id}) + '/check-occupied-room';

        return this.http
            .post(url, params, {
                headers: this.authHeaders
            })
            .then(this.getModeler());
    }

    checkInWithAssignment(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/check-in-with-assignment';

        const data = {
            accommodations: this.updateAccommodationsMapper(reservation.room_numbers),
        };

        return this.http
            .post(url, data, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    listUninvoicedItems(reservation) {

        const url = this.url({
            propertyId: reservation.property_id,
            id: reservation.id,
        }) + '/list-uninvoiced-items';

        return this.http
            .get(url, {
                headers: this.authHeaders,
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    updateAccommodationsMapper(accommodations) {

        return accommodations.map(accommodation => ({
            id: accommodation.id,
            changedFrom: accommodation.room ? accommodation.room.storedID : null,
            room: accommodation.room ? accommodation.room.id : null,
        }));
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/reservation.service.js